/**
 * Network-call capture (replicated from brospace).
 *
 * Monkey-patches `fetch` and `XMLHttpRequest` once at startup into a ring
 * buffer (last {@link MAX_ENTRIES}) recording method / url / status / duration
 * — NEVER headers or bodies (privacy). The feedback widget attaches these to a
 * GitLab issue so a bug report carries the failing request right next to it.
 *
 * Skipped: static assets, the realtime /ws socket, and our own
 * /api/feedback* calls (so submitting feedback doesn't pollute its own
 * capture). Initialised from main.tsx; no-op under MODE === 'test'.
 */
const MAX_ENTRIES = 20;

export interface NetworkEntry {
  method: string;
  url: string;
  status: number;
  duration: number; // milliseconds
}

const networkBuffer: NetworkEntry[] = [];
let initialized = false;
let _testingOverride = false;
let _originalFetch: typeof window.fetch | null = null;
let _originalXHR: typeof window.XMLHttpRequest | null = null;

const SKIP_EXTENSIONS = /\.(js|css|png|jpg|jpeg|gif|svg|webp|woff|woff2|ttf|ico|map)(\?.*)?$/i;

function shouldCapture(url: string): boolean {
  try {
    const pathname = new URL(url, location.href).pathname;
    if (pathname.startsWith('/ws')) return false;
    if (pathname.startsWith('/api/feedback')) return false;
    return !SKIP_EXTENSIONS.test(pathname);
  } catch {
    return true;
  }
}

function pushEntry(entry: NetworkEntry): void {
  networkBuffer.push(entry);
  if (networkBuffer.length > MAX_ENTRIES) networkBuffer.shift();
}

export function initNetworkCapture(): void {
  if (initialized) return;
  if (import.meta.env.MODE === 'test' && !_testingOverride) return;
  initialized = true;

  // ── fetch ──
  _originalFetch = window.fetch;
  type FetchInput = Parameters<typeof window.fetch>[0];
  window.fetch = async function (input: FetchInput, init?: Parameters<typeof window.fetch>[1]): Promise<Response> {
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url;
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();

    if (!shouldCapture(url)) return _originalFetch!(input, init);

    const start = performance.now();
    try {
      const response = await _originalFetch!(input, init);
      pushEntry({ method, url, status: response.status, duration: Math.round(performance.now() - start) });
      return response;
    } catch (err) {
      pushEntry({ method, url, status: 0, duration: Math.round(performance.now() - start) });
      throw err;
    }
  };

  // ── XMLHttpRequest (libraries / older code paths) ──
  _originalXHR = window.XMLHttpRequest;
  const OriginalXHR = window.XMLHttpRequest;
  window.XMLHttpRequest = class PatchedXHR extends OriginalXHR {
    private _captureMethod = 'GET';
    private _captureUrl = '';
    private _captureStart = 0;

    open(method: string, url: string | URL): void;
    open(method: string, url: string | URL, async: boolean, username?: string | null, password?: string | null): void;
    open(method: string, url: string | URL, async = true, username?: string | null, password?: string | null): void {
      this._captureMethod = method.toUpperCase();
      this._captureUrl = typeof url === 'string' ? url : url.href;
      super.open(method, url, async, username, password);
    }

    send(body?: Parameters<typeof OriginalXHR.prototype.send>[0]): void {
      if (shouldCapture(this._captureUrl)) {
        this._captureStart = performance.now();
        this.addEventListener('loadend', () => {
          pushEntry({
            method: this._captureMethod,
            url: this._captureUrl,
            status: this.status,
            duration: Math.round(performance.now() - this._captureStart),
          });
        });
      }
      super.send(body);
    }
  } as typeof window.XMLHttpRequest;
}

export function getNetworkCalls(): NetworkEntry[] {
  return [...networkBuffer];
}

export function clearNetworkCalls(): void {
  networkBuffer.length = 0;
}

export function _resetForTesting(): void {
  if (_originalFetch && window.fetch !== _originalFetch) window.fetch = _originalFetch;
  _originalFetch = null;
  if (_originalXHR && window.XMLHttpRequest !== _originalXHR) window.XMLHttpRequest = _originalXHR;
  _originalXHR = null;
  initialized = false;
  _testingOverride = true;
  networkBuffer.length = 0;
}
